package fyi.moko.hotpl.ui.map

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.setValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import fyi.moko.hotpl.domain.model.Place

/** JS/web actual: a Google map overlaid on the Compose canvas (see [WebMapController]). */
@Composable
actual fun PlacesMap(
    places: List<Place>,
    selectedPlaceId: String?,
    onPlaceSelect: (String) -> Unit,
    onPlaceOpen: (String) -> Unit,
    modifier: Modifier,
    displayOnly: Boolean,
    pinColors: Map<String, Color>,
    wantToGoIds: Set<String>,
) {
    val controller = rememberWebMapController(interactive = !displayOnly)
    val located = places.filter { it.latitude != null && it.longitude != null }
    // A display-only map doesn't ask the browser for the visitor's position.
    val userLocation = if (displayOnly) UserLocationState() else rememberUserLocation()

    // Markers refresh whenever the located set or the selection changes.
    LaunchedEffect(controller.ready, located, selectedPlaceId, pinColors, wantToGoIds) {
        if (controller.ready) {
            controller.setPlaceMarkers(located, selectedPlaceId, pinColors, wantToGoIds, onPlaceSelect, onPlaceOpen)
        }
    }

    // Where to point — see [resolveMapTarget]. Recentering keys off the resolved target, not
    // on every marker change, so the user's panning isn't yanked back.
    // The default zoom applies to the first center only: after that the user owns the zoom,
    // and re-applying it on every selection change would undo their zooming.
    val target = resolveMapTarget(
        places = located,
        selectedPlaceId = selectedPlaceId,
        userTarget = userLocation.target,
    )
    var centeredOnce by remember { mutableStateOf(false) }
    LaunchedEffect(controller.ready, target) {
        if (!controller.ready) return@LaunchedEffect
        val zoom = if (centeredOnce) null else MapDefaults.DEFAULT_ZOOM.toInt()
        controller.centerOn(target.latitude, target.longitude, zoom)
        centeredOnce = true
    }

    WebMapBox(controller, modifier)
}
